import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getApiHost } from '../../../../app/infrastructure/http/apiClient';
import { unwrapAuthSession } from '../../application/unwrapAuthSession';
import type {
  AuthSession,
  SignInPayload,
  SignUpPayload,
} from '../../application/types';

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: fetchBaseQuery({
    baseUrl: getApiHost(),
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
      const token = (
        getState() as { authorization?: { accessToken?: string | null } }
      ).authorization?.accessToken;
      if (token) {
        headers.set(
          'Authorization',
          token.startsWith('Bearer ') ? token : `Bearer ${token}`,
        );
      }
      return headers;
    },
  }),
  endpoints: (build) => ({
    signIn: build.mutation<AuthSession, SignInPayload>({
      transformResponse: (response: unknown) => unwrapAuthSession(response),
      query: (body) => ({
        url: '/auth/sign-in',
        method: 'POST',
        body,
      }),
    }),
    signInWithGoogle: build.mutation<AuthSession, { idToken: string }>({
      transformResponse: (response: unknown) => unwrapAuthSession(response),
      query: (body) => ({
        url: '/auth/google',
        method: 'POST',
        body,
      }),
    }),
    signUp: build.mutation<AuthSession, SignUpPayload>({
      transformResponse: (response: unknown) => unwrapAuthSession(response),
      query: (body) => ({
        url: '/auth/sign-up',
        method: 'POST',
        body,
      }),
    }),
    forgotPassword: build.mutation<unknown, void>({
      query: () => ({
        url: '/auth/forgot-password',
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useSignInMutation,
  useSignInWithGoogleMutation,
  useSignUpMutation,
  useForgotPasswordMutation,
} = authApi;
